// The account-local half of an install (02-DESIGN/14-solutions/01-the-manifest.md
// § install, § upgrade, § removal): after the solutions service's platform
// half, the installer — here the console, as an admin — writes the settings
// with the manifest's defaults, stores the secrets an admin typed, creates
// the stores with their types and indexes, and in a solo workspace writes the
// required documents and rules. Each step is a verb the admin's template
// admits and each is idempotent — *exists* is success — so an install that
// stopped halfway is finished by running it again. Completeness is computed
// from what the console can read, never kept.

import { isRefusal, Refusal, type Bus } from "../../platform/bus.ts";
import { bytes } from "../../ui/components.tsx";
import { Subjects as AuthSubjects, type TenantReply } from "../../wire/auth.ts";
import { SERVICE as AUTH } from "../../wire/identity.ts";
import { Code as ConfigCode } from "../../wire/config.ts";
import { Action, requiredSubject, SERVICE as GUARD } from "../../wire/guard.ts";
import {
  Code as StateCode,
  DEFAULT_BUDGET,
  type IndexDeclaration,
  type TypeRecord,
} from "../../wire/state.ts";
import {
  DOCUMENT_REQUIRED,
  DOCUMENT_SETTINGS,
  secretPath,
  secretPrefix,
  storeName,
  TARGET_SOLUTION,
  type Manifest,
} from "../../wire/solutions.ts";
import { listSubject, SERVICE as WORKLOADS, type ListReply } from "../../wire/workloads.ts";
import { settingsDefaults, tenantSecrets, type Missing } from "./words.ts";

/** Facts are what an install's completeness is computed from. */
export interface Facts {
  /** Whether the settings document exists; undefined where the manifest declares no tenant setting with a default. */
  settings?: boolean;
  /** The secret names that hold a version. */
  secrets: ReadonlySet<string>;
  /** The declared store names (local) that exist. */
  stores: ReadonlySet<string>;
  /** The workspace's storage and what its stores reserve, read when a declared store is missing. */
  room?: Room;
}

/**
 * Room is a workspace's storage — its plan's JetStream disk limit, -1 when
 * unbounded — and the budgets its stores already reserve against it
 * (02-DESIGN/10-state/02-the-state-service.md § the bounds).
 */
export interface Room {
  storage: number;
  reserved: number;
}

/** budgetOf is the budget a declared store is created with: its own, or the default. */
function budgetOf(s: { budget?: number }): number {
  return s.budget !== undefined && s.budget > 0 ? s.budget : DEFAULT_BUDGET;
}

/**
 * fits reports whether a store of the budget fits the room. A store's state
 * bucket is unbudgeted and needs room beyond its budget, so a budget that
 * fills the room exactly does not fit; unknown room is taken to fit.
 */
function fits(budget: number, room: Room | undefined): boolean {
  return room === undefined || room.storage < 0 || room.reserved + budget < room.storage;
}

/** noRoom says, in a clause, why a store does not fit the workspace's room. */
function noRoom(store: string, budget: number, room: Room | undefined, reason: string): string {
  if (room === undefined || room.storage < 0) {
    return `no room for the store ${store}: ${reason}`;
  }
  return (
    `no room for the store ${store}: its plan holds ${bytes(room.storage)} and its stores reserve ` +
    `${bytes(room.reserved)}, and the store needs ${bytes(budget)} and a little beyond it for its current state`
  );
}

/** NO_ROOM_ACTION is what an admin does about a store there is no room for. */
export const NO_ROOM_ACTION =
  "Delete a store you no longer need under Stores, or move the workspace to a plan with more storage, then Finish install.";

/** readRoom reads the workspace's storage and its stores' budgets; undefined when either cannot be read. */
export async function readRoom(bus: Bus): Promise<Room | undefined> {
  try {
    const [t, stores] = await Promise.all([
      bus.call<TenantReply>(AUTH, AuthSubjects.tenantGet(), undefined),
      bus.stores().listStores(),
    ]);
    const storage = t.tenant.limits?.storage;
    if (typeof storage !== "number") {
      return undefined;
    }
    return { storage, reserved: stores.reduce((n, st) => n + (st.budget > 0 ? st.budget : 0), 0) };
  } catch {
    return undefined;
  }
}

/** missingOf names, in the person's words, every step an install still lacks: empty is complete. */
export function missingOf(m: Manifest, f: Facts): Missing {
  const out: Missing = [];
  if (f.settings === false) {
    out.push("the settings are not saved");
  }
  for (const s of tenantSecrets(m)) {
    if (s.required && !f.secrets.has(s.name)) {
      out.push(`the secret ${s.name} is missing`);
    }
  }
  for (const s of m.stores ?? []) {
    if (!f.stores.has(s.name)) {
      const name = storeName(m.name, s.name);
      out.push(
        fits(budgetOf(s), f.room)
          ? `the store ${name} does not exist`
          : `there is ${noRoom(name, budgetOf(s), f.room, "its budget does not fit")}`,
      );
    }
  }
  return out;
}

const notFound = (err: unknown) => isRefusal(err, ConfigCode.notFound) || isRefusal(err, StateCode.notFound);

/** readFacts reads, as an admin, what the install's completeness is computed from. */
export async function readFacts(bus: Bus, m: Manifest): Promise<Facts> {
  const wantsSettings = Object.keys(settingsDefaults(m)).length > 0;
  let settings: boolean | undefined;
  if (wantsSettings) {
    try {
      await bus.solutionConfig().get(m.name, DOCUMENT_SETTINGS);
      settings = true;
    } catch (err) {
      if (!notFound(err)) {
        throw err;
      }
      settings = false;
    }
  }
  const secrets = new Set<string>();
  if (tenantSecrets(m).length > 0) {
    const prefix = secretPrefix(m.name);
    const reply = await bus.vault().list(prefix);
    for (const p of reply.paths) {
      if (p.version > 0 && p.path.startsWith(prefix + ".")) {
        secrets.add(p.path.slice(prefix.length + 1));
      }
    }
  }
  const stores = new Set<string>();
  for (const s of m.stores ?? []) {
    try {
      await bus.stores().getStore(storeName(m.name, s.name));
      stores.add(s.name);
    } catch (err) {
      if (!notFound(err)) {
        throw err;
      }
    }
  }
  const room = stores.size < (m.stores ?? []).length ? await readRoom(bus) : undefined;
  return { settings, secrets, stores, room };
}

/** Steps are what the console runs after the platform half. */
export interface Steps {
  /** The secrets an admin typed on the panel, by name; an empty value is left alone. */
  secrets?: Readonly<Record<string, string>>;
  /** A solo workspace writes the required documents and rules under the admin's template; a managed one never. */
  solo: boolean;
  /** The install's reference for each platform image name, from the platform profile. */
  images?: Readonly<Record<string, string>>;
}

/**
 * runSteps runs the account-local steps, each idempotent, in the design's
 * order: the settings with the defaults where no document exists — never
 * rewriting one an admin saved, which is also what an upgrade does — the
 * secrets typed, the stores with their types and indexes, and in a solo
 * workspace the required documents and rules. The first failure stops it
 * and is the caller's to show; nothing is undone.
 */
export async function runSteps(bus: Bus, m: Manifest, steps: Steps): Promise<void> {
  const defaults = settingsDefaults(m);
  if (Object.keys(defaults).length > 0) {
    try {
      await bus.solutionConfig().get(m.name, DOCUMENT_SETTINGS);
    } catch (err) {
      if (!notFound(err)) {
        throw err;
      }
      await bus.solutionConfig().set(m.name, DOCUMENT_SETTINGS, JSON.stringify(defaults, null, 2) + "\n");
    }
  }
  for (const [name, value] of Object.entries(steps.secrets ?? {})) {
    if (value !== "") {
      await bus.vault().store(secretPath(m.name, name), value);
    }
  }
  for (const s of m.stores ?? []) {
    const store = storeName(m.name, s.name);
    try {
      await bus.stores().createStore(store, {
        description: s.description,
        history: s.history,
        budget: s.budget,
      });
    } catch (err) {
      if (isRefusal(err, StateCode.budget)) {
        // The workspace's room cannot hold the store: say so, in the
        // person's words, with what to do — never the later *does not exist*.
        const room = await readRoom(bus);
        throw new Refusal(
          StateCode.budget,
          `This workspace has ${noRoom(store, budgetOf(s), room, err.message)}. ${NO_ROOM_ACTION}`,
          { cause: err },
        );
      }
      if (!isRefusal(err, StateCode.exists)) {
        throw err;
      }
    }
    for (const [typ, rec] of Object.entries(s.types ?? {})) {
      await bus.stores().defineType(store, typ, rec as TypeRecord);
    }
    for (const [index, decl] of Object.entries(s.indexes ?? {})) {
      try {
        await bus.stores().declareIndex(store, index, decl as IndexDeclaration);
      } catch (err) {
        if (!isRefusal(err, StateCode.exists)) {
          throw err;
        }
      }
    }
  }
  if (steps.solo) {
    await writeRequired(bus, m, steps.images ?? {});
  }
}

/** writeRequired writes the solution's required documents and rules under the admin's template, in a solo workspace. */
async function writeRequired(bus: Bus, m: Manifest, images: Readonly<Record<string, string>>): Promise<void> {
  const req = m.required;
  if (req === undefined) {
    return;
  }
  for (const d of req.documents ?? []) {
    const value = JSON.stringify(d.document, null, 2) + "\n";
    if (d.target === TARGET_SOLUTION) {
      await bus.solutionConfig().set(m.name, DOCUMENT_REQUIRED, value);
      continue;
    }
    const image = images[d.target];
    if (image === undefined || image === "") {
      continue; // the install ships no image under the name: nothing runs it
    }
    const list = await bus.call<ListReply>(WORKLOADS, listSubject(), undefined);
    for (const w of list.workloads) {
      if (w.image === image && w.state !== "retired") {
        await bus.configuration().set(w.id, DOCUMENT_REQUIRED, value);
      }
    }
  }
  for (const r of req.rules ?? []) {
    await bus.call(GUARD, requiredSubject(Action.set, r.id), r.rule);
  }
}

/**
 * removeSteps deletes what the install wrote under the solution's kind once
 * the service has removed it — its settings, and its required document and
 * rules in a solo workspace. Stores and secrets stay, as the design says twice.
 */
export async function removeSteps(bus: Bus, m: Manifest, solo: boolean): Promise<void> {
  const docs = await bus.solutionConfig().list(m.name);
  for (const e of docs) {
    const i = e.key.lastIndexOf(".");
    const doc = i < 0 ? e.key : e.key.slice(i + 1);
    if (doc === DOCUMENT_REQUIRED && !solo) {
      continue; // the steward's to delete, in a managed workspace
    }
    await bus.solutionConfig().delete(m.name, doc);
  }
  if (solo) {
    for (const r of m.required?.rules ?? []) {
      await bus.call(GUARD, requiredSubject(Action.delete, r.id), undefined);
    }
  }
}
